import { useRef, useState } from "react";
import { motion, useMotionValueEvent, useReducedMotion, useScroll, useSpring } from "framer-motion";
import { MapPin, Database, Users, Mail, Home, MessageSquare } from "lucide-react";
import { FLOW_STORY } from "@/data/content";

const ICONS = [MapPin, Database, Users, Mail, Home, MessageSquare];
const CAPTIONS = ["Your districts", "Audience counts", "Matched profiles", "Your campaign", "On the doormat", "A valuation"];

export default function FlowStory() {
  const ref = useRef(null);
  const reduce = useReducedMotion();
  const [lit, setLit] = useState(-1);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start 0.85", "center 0.45"] });
  const progress = useSpring(scrollYProgress, { stiffness: 90, damping: 26 });
  useMotionValueEvent(scrollYProgress, "change", (v) => setLit(Math.floor(v * 6.2) - 1));

  return (
    <section className="relative mx-auto max-w-7xl px-5 py-24 sm:px-8 lg:py-32" data-testid="flow-story-section">
      <div className="max-w-2xl">
        <p className="eyebrow">How it flows</p>
        <h2 className="mt-6 text-4xl font-extrabold leading-[1.02] tracking-[-0.045em] sm:text-5xl">From postcode to doormat.</h2>
        <p className="mt-6 text-base leading-relaxed text-white/65 sm:text-[17px]">One continuous brief — from the districts you cover to a printed conversation in a homeowner's hallway.</p>
      </div>

      <div ref={ref} className="relative mt-20">
        <div className="absolute left-[28px] top-0 h-full w-px bg-white/10 md:hidden" aria-hidden="true">
          <motion.div className="h-full w-full origin-top bg-[#96C11F]" style={{ scaleY: progress }} />
        </div>
        <div className="absolute left-[8%] right-[8%] top-[28px] hidden h-px bg-white/10 md:block" aria-hidden="true">
          <motion.div className="h-full w-full origin-left bg-gradient-to-r from-[#C6EE5A] to-[#96C11F]" style={{ scaleX: progress }} />
          {!reduce && [0, 1, 2].map((i) => (
            <motion.span key={i} className="absolute -top-[2px] h-[5px] w-[5px] rounded-full bg-[#C6EE5A] shadow-[0_0_12px_#C6EE5A]"
              animate={{ left: ["0%", "100%"], opacity: [0, 1, 1, 0] }} transition={{ duration: 6, repeat: Infinity, delay: i * 2, ease: "linear" }} />
          ))}
        </div>
        <ol className="relative grid gap-10 md:grid-cols-6 md:gap-4">
          {FLOW_STORY.map((label, i) => {
            const Icon = ICONS[i];
            const on = i <= lit;
            return (
              <li key={label} className="flex items-center gap-5 md:flex-col md:text-center" data-testid={`flow-step-${label.toLowerCase()}`}>
                <span className={`glass relative z-10 grid h-14 w-14 shrink-0 place-items-center !rounded-full transition-all duration-700 ${on ? "!border-[#96C11F]/60 !bg-[#96C11F]/15 shadow-[0_0_36px_rgba(150,193,31,0.35)]" : ""}`}>
                  <Icon className={`h-5 w-5 transition-colors duration-700 ${on ? "text-[#C6EE5A]" : "text-white/40"}`} />
                </span>
                <div>
                  <p className={`font-ui text-[11px] font-semibold uppercase tracking-[0.16em] transition-colors duration-700 ${on ? "text-white" : "text-white/40"}`}>{label}</p>
                  <p className="mt-1.5 text-[13px] text-white/45">{CAPTIONS[i]}</p>
                </div>
              </li>
            );
          })}
        </ol>
      </div>
    </section>
  );
}
